<template>
  <ThePopUp
    :is-open="isShowPopUp"
    :mask-closable="false"
    @close="isShowPopUp = false"
  >
    <div class="py-2 tablet:px-2">
      <div class="flex min-h-[260px] w-full flex-col justify-between">
        <div>
          <strong class="mb-5 block text-2xl">
            {{ t('first-part') }}
          </strong>
          <p v-if="isJP || isKR">
            {{ t('second-part-1') }}
            <strong>{{ t('other-mode-name') }}</strong>
            {{ t('second-part-2') }}
            <strong>{{ t('other-mode-name') }}</strong>
            {{ t('second-part-3') }}
            <a
              :href="contentLinkPath"
              :title="t('other-mode-name')"
              class="text-blue-400 hover:underline"
            >
              <strong>{{ t('link-name') }}</strong>
            </a>
            {{ t('second-part-4') }}
          </p>
          <p v-else>
            {{ t('second-part-1') }}
            <strong>{{ t('other-mode-name') }}</strong>
            {{ t('second-part-2') }}
            <a
              :href="contentLinkPath"
              :title="t('other-mode-name')"
              class="text-blue-400 hover:underline"
            >
              <strong>{{ t('link-name') }}</strong>
            </a>
            {{ t('second-part-3') }}
            <strong>{{ t('other-mode-name') }}</strong>
            {{ t('second-part-4') }}
          </p>
        </div>
        <div class="mt-4 flex justify-end">
          <TheButton
            :title="$t('form.confirm')"
            class="w-[180px]"
            @click="handleLogout"
          >
            <span class="text-sm">{{ $t('form.confirm') }}</span>
          </TheButton>
        </div>
      </div>
    </div>
  </ThePopUp>
</template>

<script setup lang="ts">
  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT } = runtimeConfig.public;

  const { t } = useI18n({ useScope: 'local' });
  const { isCN, isJP, isKR } = useLanguage();

  const profileStore = useProfileStore();
  const { profile } = storeToRefs(profileStore);

  const isShowPopUp = ref<boolean>(false);

  const isValidOrg = computed((): boolean => profile.value.isValidOrg);

  const contentLinkPath = computed((): string => {
    if (isCN.value) {
      return provideDomain(EnumLocale.EN_INT, ENVIRONMENT);
    }
    return provideDomain(EnumLocale.ZH_CN, ENVIRONMENT);
  });

  const handleLogout = (): void => {
    profileStore.HANDLE_LOGOUT();
  };

  onMounted(() => {
    isShowPopUp.value = !isValidOrg.value;
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-jp": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-tw": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-kr": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-americas": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-asia-pacific": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "en-emea": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "this link",
    "first-part": "Sorry, your account cannot log in.",
    "second-part-1": "The system has detected that your account belongs to",
    "second-part-2": ". You need to click",
    "second-part-3": "to log in",
    "second-part-4": "."
  },
  "ja-jp": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "WISE-Marketplace (China)",
    "link-name": "このリンク",
    "first-part": "申し訳ありませんが、お客様のアカウントではログインできません。",
    "second-part-1": "システムは、あなたのアカウントが",
    "second-part-2": "に属していることを検出しました。",
    "second-part-3": "にログインするには",
    "second-part-4": "をクリックする必要があります。"
  },
  "zh-cn": {
    "current-mode-name": "研华工业云",
    "other-mode-name": "WISE (Global)",
    "link-name": "此连结",
    "first-part": "抱歉，您的账户无法登录。",
    "second-part-1": "系统侦测您所使用的帐号属于",
    "second-part-2": "，请点选",
    "second-part-3": "至",
    "second-part-4": "登入。"
  },
  "zh-tw": {
    "current-mode-name": "WISE (Global)",
    "other-mode-name": "研華工業雲",
    "link-name": "此連結",
    "first-part": "抱歉，您的帳戶無法登入。",
    "second-part-1": "系統偵測到您所使用的帳號屬於",
    "second-part-2": "，請點擊",
    "second-part-3": "到",
    "second-part-4": "登入。"
  }
}
</i18n>
